import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useGet, useMultipleApi } from "../api/useApi";
import ProductCard from "../components/ProductCard";
import FilterSection from "../components/FilterSection";
import SelectDropdown from "../components/SelectDropdown";
import { API_ROUTES } from "../api/apiRoutes";
import { buildQueryParams, capitalizeFirstWord } from "../utils/pageHelpers";
import {
  BOX_OPTIONS,
  PAPER_OPTIONS,
  formatPaperLabel,
  ORIGIN_OPTIONS,
  CONDITION_FILTERS,
  ADVANCED_FILTERS,
} from "../data/constants";
import TopBarFilter from "../components/TopBarFilter";
import useSeo from "../seo/useSeo";
import { ROUTE_META } from "../seo/seoConfig";

// How many products are pulled from the API per request (server supports offset/limit)
const PRODUCTS_PAGE_SIZE = 24;
// Cap the staggered card animation so cards deep in a batch never wait to appear
const MAX_ANIMATION_INDEX = 11;

const AllWatchesPage = ({ brandOptions }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialFilter = searchParams.get("filter") || "all";
  const initialBrand = searchParams.get("brand") || "all";
  const initialModel = searchParams.get("model") || "all";
  const [allModelOptions, setAllModelOptions] = useState([]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSideBarClosing, setIsSidebarClosing] = useState(false);
  const boxOptions = BOX_OPTIONS.map((box) => ({
    id: box,
    name: box,
  }));
  const paperOptions = PAPER_OPTIONS.map((paper) => ({
    id: paper,
    name: formatPaperLabel(paper),
  }));
  const originOptions = ORIGIN_OPTIONS.map((origin) => ({
    id: origin,
    name: origin,
  }));

  // Filters
  const hasFetchedFilterOptionsData = useRef(false);
  const sortOptions = [
    { value: "default", label: "Sort: Default" },
    { value: "price-asc", label: "Price: Low to High" },
    { value: "price-desc", label: "Price: High to Low" },
    { value: "name-asc", label: "Name: A-Z" },
    { value: "name-desc", label: "Name: Z-A" },
  ];
  const [sortBy, setSortBy] = useState("default");
  const [filter, setFilter] = useState(initialFilter);
  const [brandFilter, setBrandFilter] = useState(initialBrand);
  const [modelFilter, setModelFilter] = useState(initialModel);
  const [productTagsList, setProductTagsList] = useState([]);

  const [filterOptions, setFilterOptions] = useState({
    dial: [],
    dialNumerals: [],
    case: [],
    caseDiameter: [],
    movement: [],
    caliber: [],
    band: [],
    bandDescription: [],
    year: [],
    box: boxOptions,
    paper: paperOptions,
    origin: originOptions,
  });

  // Advanced Filters
  const [advFilters, setAdvFilters] = useState({
    dial: "all",
    dialNumerals: "all",
    case: "all",
    caseDiameter: "all",
    movement: "all",
    caliber: "all",
    band: "all",
    bandDescription: "all",
    year: "all",
    box: "all",
    paper: "all",
    origin: "all",
  });

  const dynamicConditionFilters = [
    ...CONDITION_FILTERS,
    ...productTagsList.map((tag) => ({
      key: `tag:${tag.key}`,
      label: tag.label,
    })),
  ];

  // ─── Query helpers (declared before the query memo that consumes them) ───
  const getBrandId = useCallback(
    (name) => {
      if (name === "all") {
        return "";
      }
      const brand = brandOptions?.find((b) => b?.name === name);
      return brand ? brand.id : "0";
    },
    [brandOptions],
  );

  const handleQueryFilter = useCallback((value) => {
    if (value?.startsWith("tag:")) {
      const tagId = value.split(":")[1];
      return { tag_id: tagId };
    }

    switch (value) {
      case "men/unisex":
        return { gender: "Men's/Unisex" };
      case "women":
        return { gender: "Women's" };
      case "new":
        return { condition: "New" };
      case "pre-owned":
        return { condition: "Pre-owned" };
      default:
        return {};
    }
  }, []);

  const handleSortQueryValue = useCallback((value) => {
    switch (value) {
      case "price-asc":
        return "selling_price%asc";
      case "price-desc":
        return "selling_price%desc";
      case "name-asc":
        return "name%asc";
      case "name-desc":
        return "name%desc";
      default:
        return "id%desc";
    }
  }, []);

  // ─── Paginated product list state (infinite scroll) ───
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [supportsObserver] = useState(
    () => typeof window !== "undefined" && "IntersectionObserver" in window,
  );

  const offsetRef = useRef(0);
  const hasMoreRef = useRef(true);
  const isFetchingRef = useRef(false);
  const loadMoreErrorRef = useRef(false);
  const requestIdRef = useRef(0);
  const sentinelRef = useRef(null);

  // API Calls
  const { executeAll } = useMultipleApi();
  const { execute: getModelsOptions } = useGet(API_ROUTES.MODEL.GET_OPTIONS, {
    onSuccess: (data) => {
      setAllModelOptions(data?.value || []);
    },
    onError: (error) => {
      console.error("Error fetching models:", error);
    },
  });

  const { execute: getBandOptions } = useGet(API_ROUTES.BAND.GET_OPTIONS, {
    onSuccess: (data) => {
      const options = data?.value;
      setFilterOptions((prev) => ({ ...prev, band: options }));
    },
    onError: (error) => {
      console.error("Error fetching bands:", error);
    },
  });

  const { execute: getBandDescriptionOptions } = useGet(
    API_ROUTES.BAND_DESCRIPTION.GET_OPTIONS,
    {
      onSuccess: (data) => {
        const options = data?.value;
        const filteredBandDescriptionsOptions = options?.filter(
          (model) => model?.band_id === advFilters.band,
        );

        setFilterOptions((prev) => ({
          ...prev,
          bandDescription: filteredBandDescriptionsOptions,
        }));
      },
      onError: (error) => {
        console.error("Error fetching band descriptions:", error);
      },
    },
  );

  const { execute: getCaseOptions } = useGet(API_ROUTES.CASE.GET_OPTIONS, {
    onSuccess: (data) => {
      const options = data?.value;
      setFilterOptions((prev) => ({ ...prev, case: options }));
    },
    onError: (error) => {
      console.error("Error fetching cases:", error);
    },
  });

  const { execute: getCaseDiameterOptions } = useGet(
    API_ROUTES.CASE_DIAMETER.GET_OPTIONS,
    {
      onSuccess: (data) => {
        const options = data?.value;
        setFilterOptions((prev) => ({ ...prev, caseDiameter: options }));
      },
      onError: (error) => {
        console.error("Error fetching case diameters:", error);
      },
    },
  );

  const { execute: getDialOptions } = useGet(API_ROUTES.DIAL.GET_OPTIONS, {
    onSuccess: (data) => {
      const options = data?.value;
      setFilterOptions((prev) => ({ ...prev, dial: options }));
    },
    onError: (error) => {
      console.error("Error fetching dials:", error);
    },
  });

  const { execute: getDialNumeralsOptions } = useGet(
    API_ROUTES.DIAL_NUMERAL.GET_OPTIONS,
    {
      onSuccess: (data) => {
        const options = data?.value;
        setFilterOptions((prev) => ({ ...prev, dialNumerals: options }));
      },
      onError: (error) => {
        console.error("Error fetching dial numerals:", error);
      },
    },
  );

  const { execute: getMovementOptions } = useGet(
    API_ROUTES.MOVEMENT.GET_OPTIONS,
    {
      onSuccess: (data) => {
        const options = data?.value;
        setFilterOptions((prev) => ({ ...prev, movement: options }));
      },
      onError: (error) => {
        console.error("Error fetching movements:", error);
      },
    },
  );

  const { execute: getCaliberOptions } = useGet(
    API_ROUTES.CALIBER.GET_OPTIONS,
    {
      onSuccess: (data) => {
        const options = data?.value;
        setFilterOptions((prev) => ({ ...prev, caliber: options }));
      },
      onError: (error) => {
        console.error("Error fetching calibers:", error);
      },
    },
  );

  const { execute: getYearOptions } = useGet(API_ROUTES.YEAR.GET_OPTIONS, {
    onSuccess: (data) => {
      const options = data?.value?.map((year) => ({
        id: year,
        name: year,
      }));
      setFilterOptions((prev) => ({ ...prev, year: options }));
    },
    onError: (error) => {
      console.error("Error fetching years:", error);
    },
  });

  const { execute: getProductTags } = useGet(
    API_ROUTES.WATCHES.GET_PRODUCT_TAGS,
    {
      onSuccess: (data) => {
        const options = data?.value?.map((tag) => ({ key: tag?.id, label: tag?.name }));
        setProductTagsList(options || []);
      },
      onError: (error) => {
        console.error("Error fetching product tags:", error);
      },
    },
  );

  const handleProductsError = useCallback((error) => {
    console.error("Error fetching products:", error);
  }, []);

  const { execute: getProducts } = useGet(API_ROUTES.WATCHES.GET_ALL, {
    onError: handleProductsError,
  });

  // Everything except offset/limit — one stable object per filter combination
  const baseQuery = useMemo(
    () =>
      buildQueryParams({
        brand_id: getBrandId(brandFilter),
        model_id: modelFilter,
        band_id: advFilters.band,
        band_description_id: advFilters.bandDescription,
        case_id: advFilters.case,
        case_diameter_id: advFilters.caseDiameter,
        dial_id: advFilters.dial,
        dial_numerals_id: advFilters.dialNumerals,
        caliber_id: advFilters.caliber,
        movement_id: advFilters.movement,
        year: advFilters.year,
        box: advFilters.box,
        paper: advFilters.paper,
        origin: advFilters.origin,
        sort: handleSortQueryValue(sortBy),
        ...(filter !== "latest" ? handleQueryFilter(filter) : {}),
      }),
    [
      getBrandId,
      brandFilter,
      modelFilter,
      advFilters,
      sortBy,
      filter,
      handleSortQueryValue,
      handleQueryFilter,
    ],
  );

  // Narrowed by brand here rather than when the models arrive, because on a
  // fresh page load the models can arrive before the brands do.
  const modelOptions = useMemo(() => {
    const brandId = getBrandId(brandFilter);
    return allModelOptions.filter(
      (model) => String(model?.brand_id) === String(brandId),
    );
  }, [allModelOptions, getBrandId, brandFilter]);

  // A brand filter can only be resolved to an id once brandOptions has arrived,
  // otherwise we would fire a request with brand_id=0 and render an empty page.
  const isBrandResolvable = brandFilter === "all" || !!brandOptions?.length;

  // Serialized so the reset effect only re-runs on an actual filter change
  const queryKey = useMemo(
    () => (isBrandResolvable ? JSON.stringify(baseQuery) : null),
    [isBrandResolvable, baseQuery],
  );

  const baseQueryRef = useRef(baseQuery);
  baseQueryRef.current = baseQuery;

  const loadPage = useCallback(
    async (targetOffset, isReset) => {
      const reqId = requestIdRef.current + 1;
      requestIdRef.current = reqId;
      isFetchingRef.current = true;
      loadMoreErrorRef.current = false;
      setLoadMoreError(false);

      if (isReset) {
        setIsInitialLoading(true);
        setIsLoadingMore(false);
      } else {
        setIsLoadingMore(true);
      }

      const { data, error } = await getProducts({
        query: {
          ...baseQueryRef.current,
          offset: targetOffset,
          limit: PRODUCTS_PAGE_SIZE,
        },
      });

      // A newer request has been fired in the meantime — it now owns the state
      if (reqId !== requestIdRef.current) {
        return;
      }

      isFetchingRef.current = false;

      if (error) {
        loadMoreErrorRef.current = true;
        setLoadMoreError(true);
        setIsInitialLoading(false);
        setIsLoadingMore(false);
        if (isReset) {
          setProducts([]);
          setTotalCount(0);
          hasMoreRef.current = false;
          setHasMore(false);
        }
        return;
      }

      const batch = Array.isArray(data?.value) ? data.value : [];
      const total = Number.isFinite(data?.count) ? data.count : 0;
      const nextOffset = targetOffset + batch.length;
      const reachedEnd =
        batch.length < PRODUCTS_PAGE_SIZE || (total > 0 && nextOffset >= total);

      offsetRef.current = nextOffset;
      hasMoreRef.current = !reachedEnd;

      setTotalCount(total);
      setProducts((prev) => {
        if (isReset) {
          return batch;
        }
        const seen = new Set(prev.map((p) => p?.id));
        return [...prev, ...batch.filter((p) => !seen.has(p?.id))];
      });
      setHasMore(!reachedEnd);
      setIsInitialLoading(false);
      setIsLoadingMore(false);
    },
    [getProducts],
  );

  const handleLoadMore = useCallback(() => {
    if (
      isFetchingRef.current ||
      !hasMoreRef.current ||
      loadMoreErrorRef.current
    ) {
      return;
    }
    loadPage(offsetRef.current, false);
  }, [loadPage]);

  const handleRetryLoadMore = useCallback(() => {
    loadMoreErrorRef.current = false;
    setLoadMoreError(false);
    loadPage(offsetRef.current, false);
  }, [loadPage]);

  useEffect(() => {
    if (hasFetchedFilterOptionsData.current) {
      return;
    }

    hasFetchedFilterOptionsData.current = true;

    const fetchFilterOptionData = async () => {
      await executeAll([
        () => getBandOptions(),
        () => getCaseOptions(),
        () => getCaseDiameterOptions(),
        () => getDialOptions(),
        () => getDialNumeralsOptions(),
        () => getMovementOptions(),
        () => getCaliberOptions(),
        () => getYearOptions(),
        () => getProductTags(),
      ]);
    };
    fetchFilterOptionData();
  }, []);

  // Any filter/sort change resets the list back to the first page
  useEffect(() => {
    if (!queryKey) {
      return;
    }

    offsetRef.current = 0;
    hasMoreRef.current = true;
    setHasMore(true);
    setProducts([]);
    setTotalCount(0);
    loadPage(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  // Infinite scroll — fetch the next page as the sentinel comes into view
  useEffect(() => {
    if (!supportsObserver) {
      return;
    }

    const node = sentinelRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          handleLoadMore();
        }
      },
      { root: null, rootMargin: "400px 0px", threshold: 0 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [supportsObserver, handleLoadMore, isInitialLoading, hasMore]);

  useEffect(() => {
    if (brandFilter !== "all") {
      getModelsOptions();
    } else {
      setFilterOptions((prev) => ({ ...prev, model: [] }));
      setModelFilter("all");
    }
  }, [brandFilter]);

  useEffect(() => {
    if (advFilters.band !== "all") {
      getBandDescriptionOptions();
    } else {
      setFilterOptions((prev) => ({ ...prev, bandDescription: [] }));
      setAdvFilters((prev) => ({ ...prev, bandDescription: "all" }));
    }
  }, [advFilters.band]);

  useEffect(() => {
    setFilter(searchParams.get("filter") || "all");
    setBrandFilter(searchParams.get("brand") || "all");
    setModelFilter(searchParams.get("model") || "all");
  }, [searchParams]);

  // Handler functions
  const handleFilterChange = (filterValue) => {
    const params = new URLSearchParams(searchParams);
    if (filterValue === "latest") {
      params.delete("filter");
    } else {
      params.set("filter", filterValue);
      setFilter(filterValue);
    }
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleBrandFilterChange = (brandValue) => {
    const params = new URLSearchParams(searchParams);
    // A model belongs to one brand, so a brand change always drops it.
    params.delete("model");
    if (brandValue === "all") {
      params.delete("brand");
      setModelFilter("all");
    } else {
      params.set("brand", brandValue);
      setBrandFilter(brandValue);
      setModelFilter("all");
    }
    navigate(`?${params.toString()}`, { replace: true });
  };

  // Kept in the link so a featured model card, Back and shared links all land
  // on the same model.
  const handleModelFilterChange = (modelValue) => {
    const params = new URLSearchParams(searchParams);
    if (modelValue === "all") {
      params.delete("model");
    } else {
      params.set("model", modelValue);
    }
    setModelFilter(modelValue);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const setAdvFilter = (key, value) =>
    setAdvFilters((prev) => ({ ...prev, [key]: value }));

  const clearAllAdvFilters = () => {
    setAdvFilters({
      dial: "all",
      dialNumerals: "all",
      case: "all",
      caseDiameter: "all",
      movement: "all",
      caliber: "all",
      band: "all",
      bandDescription: "all",
      year: "all",
      box: "all",
      paper: "all",
      origin: "all",
    });
  };

  const clearAllFilterParams = () => {
    setFilter("all");
    setBrandFilter("all");
    setModelFilter("all");
    setSortBy("default");
    navigate("", { replace: true });
  };

  const toggleSidebar = () => {
    if (sidebarOpen) {
      setIsSidebarClosing(true);
      setTimeout(() => {
        setSidebarOpen(false);
        setIsSidebarClosing(false);
      }, 400);
    } else {
      setSidebarOpen(true);
    }
  };

  const getModelName = (id) => {
    const model = modelOptions?.find((m) => m.id == id);
    return model ? model.name : "";
  };

  const activeAdvCount = Object.values(advFilters).filter(
    (v) => v !== "all",
  ).length;

  const getFilterLabel = (filterValue) => {
    if (filterValue?.startsWith("tag:")) {
      const tagId = filterValue.split(":")[1];
      const tag = productTagsList.find((t) => String(t.key) === String(tagId));
      return tag ? `${capitalizeFirstWord(tag.label)} Watches` : "Watches";
    }
    switch (filterValue) {
      case "men/unisex":
        return "Men's/Unisex Watches";
      case "women":
        return "Women's Watches";
      case "new":
        return "New Watches";
      case "pre-owned":
        return "Pre-Owned Watches";
      case "unisex":
        return "Unisex Watches";
      default:
        return "All Watches";
    }
  };

  const filterLabel = getFilterLabel(filter);

  const modelName = modelFilter !== "all" ? getModelName(modelFilter) : "";
  const brandTitle = modelName ? `${brandFilter} ${modelName}` : brandFilter;

  const pageTitle =
    brandFilter !== "all"
      ? `${brandTitle} ${filterLabel != "All Watches" ? `— ${filterLabel}` : "Collection"}`
      : filterLabel;

  // Filtered views get their own title/description, but only the unfiltered
  // /watches page is canonical — filter permutations would otherwise create
  // thousands of near-duplicate URLs for Google to chew through.
  const isFilteredView =
    brandFilter !== "all" || filter !== "all" || modelFilter !== "all";

  useSeo({
    title: isFilteredView ? pageTitle : ROUTE_META["/watches"].title,
    description: isFilteredView
      ? `Browse ${totalCount ? `${totalCount.toLocaleString("id-ID")} ` : ""}authenticated ${pageTitle.toLowerCase()} at Legacy Timepieces. Every timepiece verified, with full details and pricing.`
      : ROUTE_META["/watches"].description,
    canonicalPath: "/watches",
  });

  return (
    <div style={{ minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <div className="all-watches-breadcrumb">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>{pageTitle}</span>
      </div>

      {/* Page Header */}
      <div className="section-header" style={{ paddingTop: 40 }}>
        <div className="section-label">Our Collection</div>
        <h2 className="section-title">{pageTitle}</h2>
        <div className="section-line" />
        <p className="collection-info">
          {isInitialLoading
            ? "Loading collection…"
            : `${totalCount.toLocaleString("id-ID")} timepiece${totalCount !== 1 ? "s" : ""} available`}
        </p>
      </div>

      {/* Top Bar - Filters */}
      <div className="all-watches-filters">
        <TopBarFilter
          filters={dynamicConditionFilters}
          filter={filter}
          handleFilterChange={handleFilterChange}
        />

        <div className="all-watches-sort-row">
          {/* Advanced Filter Toggle */}
          <button
            className={`filter-btn adv-filter-toggle ${activeAdvCount > 0 ? "active" : ""}`}
            onClick={toggleSidebar}
          >
            Filters{" "}
            {activeAdvCount > 0 && (
              <span className="adv-count-badge">{activeAdvCount}</span>
            )}
          </button>

          <SelectDropdown
            options={brandOptions.map((b) => ({
              value: b.name,
              label: b.name,
            }))}
            value={brandFilter}
            onChange={handleBrandFilterChange}
            placeholder="All Brands"
            allLabel="All Brands"
          />

          {/* Model Dropdown (Only show if Brand is selected) */}
          {brandFilter !== "all" && (
            <SelectDropdown
              // Ids as strings, to match the model id read from the link
              options={modelOptions.map((m) => ({
                value: String(m.id),
                label: m.name,
              }))}
              value={modelFilter}
              onChange={handleModelFilterChange}
              placeholder="All Models"
              allLabel="All Models"
            />
          )}

          {/* Sort */}
          <SelectDropdown
            options={sortOptions}
            value={sortBy}
            onChange={setSortBy}
            placeholder="Sort: Default"
            showAll={false}
          />

          {/* Clear All Button */}
          {(filter !== "all" ||
            brandFilter !== "all" ||
            modelFilter !== "all" ||
            sortBy !== "default") && (
            <button className="filter-btn" onClick={clearAllFilterParams}>
              Default all
            </button>
          )}
        </div>
      </div>

      <div
        className={`all-watches-layout ${sidebarOpen ? "sidebar-open" : ""}`}
      >
        {/* Side Bar - Advanced Filter */}
        {sidebarOpen && (
          <aside
            className={`adv-filter-sidebar ${isSideBarClosing ? "closing" : ""}`}
          >
            <div className="adv-filter-sidebar-header">
              <span>Refine</span>
              {activeAdvCount > 0 && (
                <button className="adv-clear-btn" onClick={clearAllAdvFilters}>
                  Clear all
                </button>
              )}
            </div>
            {ADVANCED_FILTERS.map((f) => {
              // Only show Band Description filter if Band is selected
              if (f.key === "bandDescription" && advFilters.band === "all") {
                return null;
              }

              return (
                <FilterSection
                  key={f.key}
                  label={f.label}
                  options={filterOptions[f.key] || []}
                  active={advFilters[f.key]}
                  onChange={(val) => setAdvFilter(f.key, val)}
                />
              );
            })}
          </aside>
        )}

        {/* Product Grid */}
        {isInitialLoading ? (
          <div
            className={`all-watches-loading spinner-container ${sidebarOpen ? "with-sidebar" : ""}`}
          >
            <div className="spinner" />
          </div>
        ) : products?.length > 0 ? (
          <div
            className={`all-watches-results ${sidebarOpen ? "with-sidebar" : ""}`}
          >
            <div className={`product-grid ${sidebarOpen ? "with-sidebar" : ""}`}>
              {products.map((w, i) => (
                <ProductCard
                  key={w?.id}
                  watch={w}
                  index={Math.min(i % PRODUCTS_PAGE_SIZE, MAX_ANIMATION_INDEX)}
                />
              ))}
            </div>

            {/* Infinite scroll trigger + status */}
            <div
              ref={sentinelRef}
              className="infinite-sentinel"
              aria-hidden="true"
            />
            <div className="infinite-status" aria-live="polite">
              {isLoadingMore && (
                <>
                  <div className="spinner spinner--sm" />
                  <span className="infinite-status-text">
                    Loading more timepieces…
                  </span>
                </>
              )}

              {!isLoadingMore && loadMoreError && (
                <>
                  <span className="infinite-status-text">
                    Couldn't load more timepieces.
                  </span>
                  <button className="filter-btn" onClick={handleRetryLoadMore}>
                    Try again
                  </button>
                </>
              )}

              {!isLoadingMore && !loadMoreError && hasMore && !supportsObserver && (
                <button className="filter-btn" onClick={handleLoadMore}>
                  Load more
                </button>
              )}

              {!isLoadingMore && !loadMoreError && !hasMore && (
                <span className="infinite-status-text">
                  Showing all {totalCount.toLocaleString("id-ID")} timepieces
                </span>
              )}
            </div>
          </div>
        ) : (
          <div
            className={`all-watches-empty ${sidebarOpen ? "with-sidebar" : ""}`}
          >
            <div className="empty-state-icon-large">⌚</div>
            <div className="empty-state-title-serif">No watches found</div>
            <div style={{ fontSize: 14 }}>Try adjusting your filters</div>
            <button
              className="filter-btn"
              style={{ marginTop: 20 }}
              onClick={() => {
                clearAllAdvFilters();
                clearAllFilterParams();
              }}
            >
              Clear all filter
            </button>
          </div>
        )}
      </div>

      <div style={{ height: 80 }} />
    </div>
  );
};

export default AllWatchesPage;
